
    import Vue from "vue";
    import Component from "vue-class-component";
    import Store from "@/store/store";
    import * as toastr from "toastr";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import BriefingProgressIcons from "@/vue/components/BriefingProgressIcons.vue";
    import BriefingProgressBar from "@/vue/components/BriefingProgressBar.vue";
    import TextPart from "@/vue/components/PageParts/TextPart.vue";
    import NotePart from "@/vue/components/PageParts/NotePart.vue";
    import MapPartLeaflet from "@/vue/components/PageParts/MapPartLeaflet.vue";
    import MapPartGoogle from "@/vue/components/PageParts/MapPartGoogle.vue";
    import ResourceAndTextPart from "@/vue/components/PageParts/ResourceAndTextPart.vue";
    import RadioChoicePart from "@/vue/components/PageParts/RadioChoicePart.vue";
    import CheckboxChoicesPart from "@/vue/components/PageParts/CheckboxChoicesPart.vue";
    import FreeTextPart from "@/vue/components/PageParts/FreeTextPart.vue";
    import DownloadPart from "@/vue/components/PageParts/DownloadPart.vue";
    import PhotoCapturePart from "@/vue/components/PageParts/PhotoCapturePart.vue";
    import ConfirmToContinuePart from "@/vue/components/PageParts/ConfirmToContinuePart.vue";
    import PasscodeToContinuePart from "@/vue/components/PageParts/PasscodeToContinuePart.vue";
    import ContactDialogue from "@/vue/components/ContactDialogue.vue";
    import NumericId from "@/vue/components/NumericId.vue";
    import { PagePartComponent, TriState, firstPageID, previewVisitID } from "@/model/Enums";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { IBriefingResponse } from "@/model/IBriefingResponse";
    import { BriefingProgress, ProgressStage } from "@/model/BriefingProgress";
    import { Site } from "@/model/Site";
    import { Pathway } from "@/model/Pathway";
    import { Visit } from "@/model/Visit";
    import { Page } from "@/model/Page";
    import { PagePart } from "@/model/PagePart";
    import { VisitAnswer } from "@/model/VisitAnswer";
    import { RadioChoiceOptions } from "@/model/pagePartOptions/RadioChoiceOptions";
    import { CheckboxChoicesOptions } from "@/model/pagePartOptions/CheckboxChoicesOptions";
    import { FreeTextOptions } from "@/model/pagePartOptions/FreeTextOptions";
    import { PhotoCaptureOptions } from "@/model/pagePartOptions/PhotoCaptureOptions";
    import { ConfirmToContinueOptions } from "@/model/pagePartOptions/ConfirmToContinueOptions";
    import { PasscodeToContinueOptions } from "@/model/pagePartOptions/PasscodeToContinueOptions";

    @Component({ components: {
            ApiButton,
            PrimaryButton,
            ConfirmDialogue,
            DatePicker,
            BriefingProgressIcons,
            BriefingProgressBar,
            TextPart,
            NotePart,
            MapPartLeaflet,
            MapPartGoogle,
            ResourceAndTextPart,
            RadioChoicePart,
            CheckboxChoicesPart,
            FreeTextPart,
            DownloadPart,
            PhotoCapturePart,
            ConfirmToContinuePart,
            PasscodeToContinuePart,
            NumericId,
            ContactDialogue
        }})
    export default class Briefing extends Vue {

        async mounted(): Promise<void> {
            this.progress.setResizeObserver("briefing-resize-observer");
            this.visitID = this.$route.params.visitID ?? Utils.emptyGuidValue;
            this.previewPathwayID = Utils.getUrlQueryValue(this.$route, "previewPathwayID");
            this.pageID = this.$route.params.pageID ?? Utils.emptyGuidValue;
            this.gotoPage(this.pageID);
        }

        //
        // -- properties
        //

        // @Ref("pageForm") private readonly pageForm!: VForm;

        showNavButtons: boolean = false;
        shouldShowMissingAnswerMessage: boolean = false;
        visitID: string = "";
        pageID: string = "";
        previewPathwayID: string = "";

        site: Site = new Site();
        pathway: Pathway = new Pathway();
        visit: Visit = new Visit();
        page: Page = new Page();
        progress: BriefingProgress = new BriefingProgress();

        isBusySaving: boolean = false;
        shouldShowMissingMessages: boolean = false;
        hasLoaded: boolean = false;
        hasLinkExpired: boolean = false;
        shouldShowContactDialogue: boolean = false;

        // we don't want to pass an empty ID to progress component
        // - we want to specifically tell it it's the first page or it won't call load
        get progressPageID(): string {
            return Utils.isEmptyId(this.pageID) ? firstPageID : this.pageID;
        }

        get isSiteID(): boolean {
            return !Utils.isEmptyId(this.site?.id);
        }

        get canDelete(): boolean {
            return !Utils.isEmptyId(this.page.id);
        }

        get isAPreviousPage(): boolean {
            return !Utils.isEmptyId(this.page.previousID);
        }

        get isANextPage(): boolean {
            return !Utils.isEmptyId(this.page.nextID);
        }

        get isPreviewMode(): boolean {
            return !Utils.isEmptyId(this.previewPathwayID) && this.visitID === previewVisitID;
        }

        get isTestNext(): boolean {
            return this.isANextPage && this.progress?.quizStartsAtPageID === this.page?.nextID;
        }

        get isTestStart(): boolean {
            return this.progress?.quizStartsAtPageID === this.page?.id;
        }

        get shouldShowContent(): boolean {
            return !this.hasLinkExpired
                && ((this.progress.stage === ProgressStage.Briefing && !Utils.hasDateValue(this.visit.testStarted))
                    || (this.progress.stage === ProgressStage.Test && !Utils.hasDateValue(this.visit.completed)));
        }

        get stageText(): string {
            return ProgressStage[this.progress.stage];
        }

        get shouldShowForbiddenTestStarted(): boolean {
            return Utils.hasDateValue(this.visit.testStarted) && !Utils.hasDateValue(this.visit.completed) && this.progress.stage === ProgressStage.Briefing;
        }

        get shouldShowForbiddenComplete(): boolean {
            return Utils.hasDateValue(this.visit.completed);
        }

        //
        // -- methods
        //

        // do this instead of changing route (like reloading page)
        //  - means progress indicator can stay-put and animate properly
        async gotoPage(pageID: string): Promise<void> {
            this.showNavButtons = false;
            this.hasLoaded = false;
            this.pageID = pageID;
            this.load();
            setTimeout(() => {
                this.showNavButtons = true;
            }, 1000);
        }

        async load(): Promise<void> {
            this.shouldShowMissingMessages = false;
            const pageID = Utils.isEmptyId(this.pageID) ? firstPageID : this.pageID;
            const path = this.isPreviewMode
                ? `/api/page/briefing?visitID=${this.visitID}&pageID=${pageID}&previewPathwayID=${this.previewPathwayID}`
                : `/api/page/briefing?visitID=${this.visitID}&pageID=${pageID}`;
            const data: IBriefingResponse = await ApiClient.get(path);
            this.hasLinkExpired = data.hasExpired;

            // if old, deleted visit - or duff link - visit (and other stuff) might be null, so bail out.
            if(data.visit == null) return;

            // Is it a redo? - change URL to suit
            // (see PageLogic.Briefing on server - only triggered on FIRST page)
            if(data.visit.id != this.visitID) {
                //let url = `/briefing/${this.visitID}/${this.page.nextID}`;
                let url = window.location.toString();
                url = url.replace(this.visitID, data.visit.id);
                history.pushState({}, document.title, url);
                this.visitID = data.visit.id;
            }

            // create any empty answers array so we don't have to add them later (and so reactivity works?)
            // (expecting ONE page!)
            if(data.pages.length === 0) console.warn("There are NO briefing pages!");
            else for (const part of data.pages[0].parts) {
                if (!data.visit.answers.some(a => a.pagePartID === part.id)) {
                    data.visit.answers.push(VisitAnswer.create(this.visitID, part.id));
                }
            }

            this.site.update(data.site);
            this.pathway.update(data.pathway);
            this.page.update(data.pages[0]);
            this.visit.update(data.visit);
            this.progress.update(data.progress);
            Vue.nextTick(() => {
                this.hasLoaded = true;
            });
            await Store.dispatch("setSite", this.site);
        }

        async goToPrevious(): Promise<void> {
            await this.saveAnswers();

            // don't use router 'cos it's clunky and progress animation breaks...
            // (Hmm... doesn't 'pop' browser history like 'back' does...)
            let url = `/briefing/${this.visitID}/${this.page.previousID}`;
            if(this.isPreviewMode) url += `?previewPathwayID=${this.previewPathwayID}`
            history.pushState({}, document.title, url);
            this.gotoPage(this.page.previousID);
        }

        async goToNext(): Promise<void> {
            // start showing messages once the user has decided they're done
            this.shouldShowMissingMessages = true;

            const isQuestion = this.page.parts.some(p => p.isQuestion);
            const countMissingAnswer = isQuestion ? this.checkIsMissingAnswer() : 0;

            if(countMissingAnswer > 0) {
                toastr.warning(countMissingAnswer === 1 ? "There is a missing answer" : "There are some answers missing");
                return;
            }
            if(isQuestion) {
                await this.saveAnswers();
            }
            // note - gotoPage doesn't use router.  router.push re-renders the whole page (clunky) and progress animation doesn't get chance to work
            let url = `/briefing/${this.visitID}/${this.page.nextID}`;
            if(this.isPreviewMode) url += `?previewPathwayID=${this.previewPathwayID}`
            history.pushState({}, document.title, url);
            this.gotoPage(this.page.nextID);
        }

        async startTest(): Promise<void> {
            if(this.isPreviewMode) {
                await this.goToNext();
            }
            else{
                const response: ISaveResponse = await ApiClient.post("/api/visit/startTest", this.visit, "start-test");
                if(response.isSuccess) await this.goToNext();
            }
        }

        // similar to goToNext()
        async finishTest(): Promise<void> {
            // start showing messages once the user has decided they're done
            this.shouldShowMissingMessages = true;

            const isQuestion = this.page.parts.some(p => p.isQuestion);
            const countMissingAnswer = isQuestion ? this.checkIsMissingAnswer() : 0;

            if(countMissingAnswer > 0) {
                toastr.warning(countMissingAnswer === 1 ? "There is a missing answer" : "There are some answers missing");
                return;
            }

            if(isQuestion) {
                await this.saveAnswers();
            }

            let newRedoVisitID = Utils.emptyGuidValue;
            if(!this.isPreviewMode) {
                // marks the test, sends email, flags complete and returns redo-ID if needed
                const response: ISaveResponse = await ApiClient.post("/api/visit/finishTest", this.visit, "finish-test");
                newRedoVisitID = response.newID;
            }

            const routeParameters: Record<string, string> = { visitID: this.visitID };
            const routeQuery: Record<string, string> = this.isPreviewMode
                ? { previewPathwayID: this.previewPathwayID }
                : Utils.isEmptyId(newRedoVisitID)
                    ? { }
                    : { newRedoVisitID };
            this.$router.push({ name: "BriefingDone", params: routeParameters, query: routeQuery });
        }

        needsAnAnswer(): boolean {
            return this.page.parts.some(p => p.needsAnAnswer);
        }

        // Are there any page parts needing an answer that haven't got one?
        //  - return count
        //  - set flag so 'error' message for the page-part shows (and we want it to)
        checkIsMissingAnswer(): number {
            const hasAnAnswer = (partID: string): boolean => this.visit.answers.find(r => r.pagePartID === partID)?.isAnswered ?? false;
            // in VUE 2 it seems we need to use SPLICE to modify the array for the change to be reacted to!?
            // (Array.splice is overrriden so that reactivity can happen)
            for(let i = 0; i < this.page.parts.length; i++) {
                const part = this.page.parts[i];
                if(!part.options) continue;
                const isMissingAnswer = part.needsAnAnswer && !hasAnAnswer(part.id);
                if(isMissingAnswer !== part.options.shouldShowMissingMessage) {
                    // we're just *changing* a part.options property here - all the bollocks is so that Vue reacts to it:
                    part.options.shouldShowMissingMessage = this.shouldShowMissingMessages && part.needsAnAnswer && !hasAnAnswer(part.id);
                    this.page.parts.splice(i, 1, new PagePart(part))
                }
            }
            return this.page.parts.filter(p => p.options.shouldShowMissingMessage).length;
        }

        answerForPart(pagePartID: string): VisitAnswer {
            return this.visit.answers.find(r => r.pagePartID === pagePartID) ?? new VisitAnswer(VisitAnswer.create(this.visitID, pagePartID));
        }

        // event handler
        onAnswerChanged(index: number|Array<number>, pagePartID: string, answerText: string): void {
            if(!this.hasLoaded) {
                return;
            }
            const answer = this.answerForPart(pagePartID);
            if(answer == null) return;
            const pagePart = this.page.parts.find(p => p.id === pagePartID);
            if(pagePart == null) return;
            switch(pagePart.component) {
                case PagePartComponent.RadioChoice:
                    this.radioAnswerChanged(pagePart, answer, answerText, index as number);
                    break;
                case PagePartComponent.CheckboxChoices:
                    this.checkboxAnswerChanged(pagePart, answer, answerText, index as Array<number>);
                    break;
                case PagePartComponent.FreeText:
                    this.freeTextAnswerChanged(pagePart, answer, answerText);
                    break;
                case PagePartComponent.PhotoCapture:
                    // answerText contains the photoResourceID
                    this.photoCaptureAnswerChanged(pagePart, answer, answerText);
                    break;
                case PagePartComponent.ConfirmToContinue:
                    this.confirmToContinueAnswerChanged(pagePart, answer, answerText);
                    break;
                case PagePartComponent.PasscodeToContinue:
                    this.passcodeToContinueAnswerChanged(pagePart, answer, answerText);
                    break;
            }
        }

        radioAnswerChanged(pagePart: PagePart, answer: VisitAnswer, answerText: string, answerIndex: number): void {
            const radioChoiceOptions = pagePart.options as RadioChoiceOptions;
            if(!Array.isArray(radioChoiceOptions.choices)) return;
            answer.givenAnswer = answerText;
            answer.question = radioChoiceOptions.html; // we could convert this to plain text (?)
            answer.isCorrect = answerIndex === radioChoiceOptions.correctIndex ? TriState.True : TriState.False;
            answer.correctAnswer = radioChoiceOptions.correctAnswer;
            answer.isTest =  this.progress.stage === ProgressStage.Test;
            answer.isRedLine = radioChoiceOptions.isRedLineQuestion;
            this.checkIsMissingAnswer();
        }

        checkboxAnswerChanged(pagePart: PagePart, answer: VisitAnswer, answerText: string, indeces: Array<number>): void {
            const checkboxChoiceOptions = pagePart.options as CheckboxChoicesOptions;
            if(!Array.isArray(checkboxChoiceOptions.choices)) return;
            answer.givenAnswer = answerText;
            answer.question = checkboxChoiceOptions.html; // we could convert this to plain text (?)
            answer.isCorrect =
                checkboxChoiceOptions.correctIndeces.length === 0 ||
                indeces.sort().join("|") === checkboxChoiceOptions.correctIndeces.sort().join("|")
                    ? TriState.True
                    : TriState.False;
            answer.correctAnswer = checkboxChoiceOptions.correctIndeces.map(i => checkboxChoiceOptions.choices[i]).join("|");
            answer.isTest =  this.progress.stage === ProgressStage.Test;
            answer.isRedLine = checkboxChoiceOptions.isRedLineQuestion;
            this.checkIsMissingAnswer();
        }

        freeTextAnswerChanged(pagePart: PagePart, answer: VisitAnswer, answerText: string): void {
            const freeTextOptions = pagePart.options as FreeTextOptions;
            answer.givenAnswer = answerText;
            answer.question = freeTextOptions.html; // convert to plain text ????
            answer.isCorrect = TriState.UseDefault; // no 'correct' answers (currently - or ever?)
            answer.isTest =  this.progress.stage === ProgressStage.Test;
            answer.isRedLine = freeTextOptions.isRedLineQuestion;
        }

        photoCaptureAnswerChanged(pagePart: PagePart, answer: VisitAnswer, answerText: string): void {
            const didUploadFile = !Utils.isEmptyOrWhitespace(answerText);  // answer text is the file name
            const photoCaptureOptions = pagePart.options as PhotoCaptureOptions;
            //answer.givenAnswer = didUploadFile ? `Photo supplied: ${answerText}` : "No photo";
            answer.givenAnswer = didUploadFile ? "Photo supplied" : "No photo";
            answer.question = photoCaptureOptions.html;
            answer.isCorrect = didUploadFile ? TriState.True : TriState.UseDefault; // 'correct' id photo exists, else not answered
            answer.isTest = this.progress.stage === ProgressStage.Test;
            answer.isRedLine = photoCaptureOptions.isRedLineQuestion;
        }

        confirmToContinueAnswerChanged(pagePart: PagePart, answer: VisitAnswer, answerText: string): void {
            const confirmToContinueOptions = pagePart.options as ConfirmToContinueOptions;
            answer.givenAnswer = answerText;
            answer.question = confirmToContinueOptions.html;
            // we need to use TriState.UseDefault for FALSE so that it has is NO ANSWER (false *is* an answer) in checkIsMissingAnswer()
            answer.isCorrect = ConfirmToContinueOptions.IsCorrect(answerText)
                ? TriState.True
                : TriState.UseDefault;
            answer.isTest = this.progress.stage === ProgressStage.Test;
            answer.isRedLine = confirmToContinueOptions.isRedLineQuestion;
        }

        passcodeToContinueAnswerChanged(pagePart: PagePart, answer: VisitAnswer, answerText: string): void {
            const passcodeToContinueOptions = pagePart.options as PasscodeToContinueOptions;
            answer.givenAnswer = answerText;
            answer.question = passcodeToContinueOptions.html;

// console.log("+++ passcodeToContinueAnswerChanged ");
// console.log("    - answer = ", answerText);
// console.log("    - opts = ", passcodeToContinueOptions);

            // if incorrect, return UseDefault NOT False so that process is effectively halted until we get the correct code
            answer.isCorrect = answerText.toLowerCase() === passcodeToContinueOptions.passcode.toLowerCase()
                ? TriState.True
                : TriState.UseDefault;
            answer.isTest = this.progress.stage === ProgressStage.Test;
            answer.isRedLine = passcodeToContinueOptions.isRedLineQuestion;
        }

        // in practice, we're only expecting one question per page - but I want to be able to deal with multiple if needed
        // so just fire a response for each one if need be (even though it's be slow for 'many' questions)
        async saveAnswers(): Promise<void> {
            if(this.isPreviewMode) {
                toastr.info("Nothing Saved", "Preview Mode");
                return;
            }
            this.isBusySaving = true;
            for(const part of this.page.parts.filter(p => p.isQuestion)) {
                const visitAnswer = this.answerForPart(part.id);
                const response: ISaveResponse = await ApiClient.post("/api/visit/saveAnswer", visitAnswer, "answer-save");
            }
            this.isBusySaving = false;
        }
    }
